"use client";

import { Link } from "@/base/component/link";
import { Accordion, AccordionItem } from "@heroui/react";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import Script from "next/script";

const faqItems = [
  {
    id: "what-is-seo-ai-pal",
    question: "What is SEO AI Pal?",
    preview:
      "AI-powered platform for website SEO optimization, analysis, and content monitoring.",
    answer:
      "I created SEO AI Pal because I wanted a smarter way to optimize websites and stay updated on industry trends. The platform leverages state-of-the-art AI technology, combining PageSpeed Insights, DataForSEO metrics, HTML source code examination, and content monitoring to generate comprehensive optimization recommendations and keep you informed about relevant industry content.",
  },
  {
    id: "how-does-it-work",
    question: "How does the analysis work?",
    preview: "Uses PageSpeed Insights, SEO metrics, and AI analysis.",
    answer:
      "The platform works through a multi-step process: It retrieves performance metrics through PageSpeed Insights, gathers competitive data via DataForSEO, analyzes the page's HTML source code, and processes these inputs through advanced AI language models to generate tailored recommendations.",
  },
  {
    id: "features",
    question: "What insights does it provide?",
    preview:
      "Performance metrics, AI-powered SEO recommendations, and content monitoring.",
    answer:
      "The tool delivers detailed insights by combining key components: PageSpeed performance metrics, competitive SEO data (keywords, rankings, backlinks), HTML structure analysis, content monitoring from top industry websites, and AI-powered recommendations. This comprehensive approach ensures you get actionable insights based on real market data and stay updated on industry trends.",
  },
  {
    id: "ai-suggestions",
    question: "How does the AI generate recommendations?",
    preview: "Advanced AI analysis of performance data and HTML patterns.",
    answer:
      "The system utilizes advanced language models with specialized prompts for SEO analysis. These models process PageSpeed data and HTML code patterns, comparing them against current SEO best practices. This combination allows for generating specific, contextual recommendations based on your page's unique characteristics.",
  },
  {
    id: "content-monitoring",
    question: "How does content monitoring work?",
    preview: "Tracks keywords on selected websites and sends email alerts.",
    answer:
      "The content monitoring feature scans selected industry websites every 30 minutes for new articles containing your tracked keywords. When matching content is found, the system extracts key information like headlines and sends it directly to your email, keeping you informed about the latest industry developments relevant to your business.",
  },
  {
    id: "website-selection",
    question: "Can I choose which websites to monitor?",
    preview: "Yes, you can select specific websites to track.",
    answer:
      "Absolutely. You can provide a list of trusted industry websites that you want to monitor for new content. This allows you to focus on sources that are most relevant to your business and industry, ensuring you only receive alerts about content that matters to you.",
  },
  {
    id: "technical-requirements",
    question: "What do I need to start?",
    preview: "Just a public website URL and keywords to track.",
    answer:
      "For SEO analysis, all that's needed is a public URL. For content monitoring, you'll need to provide keywords you want to track and websites you want to monitor. The platform handles everything else automatically.",
  },
  {
    id: "implementation-time",
    question: "How fast is the analysis?",
    preview: "Results in under 120 seconds.",
    answer:
      "The analysis typically takes 1-2 minutes to complete. This time allows for thorough PageSpeed data gathering and HTML structure examination. Results and recommendations become available immediately after the analysis.",
  },
  {
    id: "data-accuracy",
    question: "How accurate are the recommendations?",
    preview: "Based on PageSpeed data and HTML analysis.",
    answer:
      "Each recommendation is based on concrete data from PageSpeed Insights and actual HTML code analysis - no guesswork involved. While the suggestions are reliable, it's good practice to test changes in a staging environment before implementing them live.",
  },
  {
    id: "data-privacy",
    question: "How is my data handled?",
    preview: "Only processes public website information.",
    answer:
      "The platform only processes publicly available information from websites and PageSpeed Insights. Analysis happens in real-time, all information is used solely for generating recommendations and remains private.",
  },
];

export default function Faq() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer,
      },
    })),
  };

  return (
    <>
      <Script id="faq-schema" type="application/ld+json">
        {JSON.stringify(schemaData)}
      </Script>
      <section id="faq" className="flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-8">Frequently asked questions</h2>

        <Accordion variant="shadow" className="gap-4">
          {faqItems.map((item) => (
            <AccordionItem
              key={item.question}
              aria-label={item.question}
              title={item.question}
              className="rounded-lg leading-none pt-1"
              indicator={({ isOpen }) => (
                <span className="text-2xl">
                  {isOpen ? <IconMinus /> : <IconPlus />}
                </span>
              )}
            >
              <div className="text-default-500 pb-2 leading-snug">
                <p className="font-medium mb-2">{item.preview}</p>
                {item.answer}
              </div>
              <Link href={`/faq/${item.id}`}>Read more</Link>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </>
  );
}
